"use client";

import { useCallback, useEffect, useState } from "react";
import type { BrandWizardProgressEntry } from "../../brand/brand-onboarding.helpers";
import { parseBrandResumeStep } from "../../brand/brand-onboarding.helpers";
import { ExpressBrandOnboardingWizard } from "../../brand/express-brand-onboarding-wizard";
import { BrandLoopList } from "../../brand/steps/brand-wizard-steps";

type BrandLoopStepProps = {
  operatesMultipleBrands: boolean;
  completedBrandIds: string[];
  brandWizardProgress?: Record<string, BrandWizardProgressEntry>;
  onBrandCompleted: (brandId: string) => void;
  onWizardActiveChange?: (active: boolean) => void;
};

export function BrandLoopStep({
  operatesMultipleBrands,
  completedBrandIds,
  onBrandCompleted,
  onWizardActiveChange,
}: BrandLoopStepProps) {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeBrandId, setActiveBrandId] = useState<string | undefined>();
  const [isAddingBrand, setIsAddingBrand] = useState(false);
  const [resumeStepId, setResumeStepId] = useState<
    ReturnType<typeof parseBrandResumeStep> | undefined
  >();

  const singleBrandMode = !operatesMultipleBrands;
  const needsInitialSetup =
    singleBrandMode && completedBrandIds.length === 0;

  const setWizardActive = useCallback(
    (active: boolean) => {
      onWizardActiveChange?.(active);
    },
    [onWizardActiveChange]
  );

  const openWizard = useCallback(
    (options?: { brandId?: string; resumeStepId?: ReturnType<typeof parseBrandResumeStep> }) => {
      setActiveBrandId(options?.brandId);
      setResumeStepId(options?.resumeStepId);
      setIsAddingBrand(!options?.brandId);
      setWizardOpen(true);
      setWizardActive(true);
    },
    [setWizardActive]
  );

  const closeWizard = useCallback(() => {
    setWizardOpen(false);
    setActiveBrandId(undefined);
    setIsAddingBrand(false);
    setResumeStepId(undefined);
    setWizardActive(false);
  }, [setWizardActive]);

  useEffect(() => {
    return () => setWizardActive(false);
  }, [setWizardActive]);

  const handleWizardComplete = useCallback(
    (brandId: string) => {
      onBrandCompleted(brandId);
      closeWizard();
    },
    [closeWizard, onBrandCompleted]
  );

  const handleEditBrand = useCallback(
    async (brandId: string) => {
      openWizard({
        brandId,
        resumeStepId: "brand-basics",
      });
    },
    [openWizard]
  );

  return (
    <>
      <BrandLoopList
        completedBrandIds={completedBrandIds}
        singleBrandMode={singleBrandMode}
        needsInitialSetup={needsInitialSetup}
        onAddBrand={() => openWizard()}
        onEditBrand={handleEditBrand}
      />

      {wizardOpen ? (
        <ExpressBrandOnboardingWizard
          key={`${activeBrandId ?? "new"}-${resumeStepId ?? "brand-basics"}`}
          open
          embedded={false}
          brandId={activeBrandId}
          initialStepId={resumeStepId}
          singleBrandMode={singleBrandMode && !activeBrandId && isAddingBrand}
          prefillFromWorkspace={singleBrandMode && !activeBrandId}
          onComplete={handleWizardComplete}
          onCancel={closeWizard}
        />
      ) : null}
    </>
  );
}
